import React from 'react';
import styles from './css/index.module.css';
import { Input, Textarea, Tooltip, Select, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { Delete20Regular, Info12Filled, ChevronDown16Regular, Search20Regular } from '@fluentui/react-icons';
import { validExtensions } from '../../utils/misc';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddOrganisationDetail = ({ compareObjectsValues }) => {
    const dispatch = useDispatch();
    const projectDataState = useSelector(state => state.projectData);
    const { projectDetailButtonLoading, selectedProject } = projectDataState;

    // Debug Redux state
    React.useEffect(() => {
        console.log('Redux state changed:', projectDataState);
        console.log('selectedProject from Redux:', selectedProject);
    }, [projectDataState, selectedProject]);
    const fileInputRef = React.useRef();
    const [isDragging, setIsDragging] = React.useState(false);
    const [uploadedFile, setUploadedFile] = React.useState();
    const [isComponentMounted, setIsComponentMounted] = React.useState(false);

    React.useEffect(() => {
        setIsComponentMounted(true);
        console.log('AddOrganisationDetail component mounted');
        return () => {
            setIsComponentMounted(false);
            console.log('AddOrganisationDetail component unmounted');
        };
    }, []);
    const [formData, setFormData] = React.useState({
        sector: '',
        established_year: '',
        description: '',
        employees: '',
        company_size: '',
        linkedin: '',
        base_logo: '',
        company_name: '',
        website: '',
        currency: '',
        revenue: '',
        business_type: '',
        product_website: '',
        companieshouse: '',
    });
    const [quillContent, setQuillContent] = React.useState('');

    const handleChange = e => {
        console.log('handleChange called:', e.target.name, e.target.value);
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async e => {
        e.preventDefault();
        let data = new FormData();
        if (uploadedFile) {
            data.append('logo', uploadedFile);
        }
        data.append('linkedin', formData?.linkedin);
        data.append('sector', formData?.sector);
        data.append('established_year', formData?.established_year);
        data.append('description', quillContent); // Use quillContent instead of formData.description
        data.append('employees', formData?.employees);
        data.append('company_size', formData?.company_size);
        data.append('company_name', formData?.company_name);
        data.append('website', formData?.website);
        data.append('currency', formData?.currency);
        data.append('revenue', formData?.revenue);
        data.append('business_type', formData?.business_type);
        data.append('product_website', formData?.product_website);
        data.append('companieshouse', formData?.companieshouse);

        await dispatch(PatchProjectData({ data: data, id: selectedProject?.id }));
        setUploadedFile();
    };

    const handleDivClick = () => {
        fileInputRef.current.click();
    };

    const processFile = file => {
        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (file && validImageTypes.includes(file.type)) {
            setUploadedFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result;
                setFormData({ ...formData, base_logo: base64 });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFileChange = e => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles?.length > 0) {
            processFile(selectedFiles[0]);
        }
    };

    const handleDragEnter = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files?.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                processFile(file);
            }
        }
    };

    const handleSearchOnWeb = platform => {
        let withoutSpecialChracterString = selectedProject?.company_name.replace(/[^a-zA-Z0-9\s]/g, '');
        const searchQuery = (withoutSpecialChracterString + ' ' + platform).replace(' ', '+');
        window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
    };

    const handleSetFormValues = React.useCallback(() => {
        console.log('handleSetFormValues called with selectedProject:', selectedProject);
        console.log('handleSetFormValues - selectedProject.company_name:', selectedProject?.company_name);
        console.log('handleSetFormValues - selectedProject.website:', selectedProject?.website);

        const newFormData = {
            sector: selectedProject?.sector || '',
            established_year: selectedProject?.established_year || '',
            description: '', // Keep description empty in formData
            company_size: selectedProject?.company_size || '',
            employees: selectedProject?.employees || '',
            base_logo: selectedProject?.base_logo || '',
            linkedin: selectedProject?.linkedin || '',
            company_name: selectedProject?.company_name || '',
            website: selectedProject?.website || '',
            currency: selectedProject?.currency || '',
            revenue: selectedProject?.revenue || 0, // Keep as number, not string
            business_type: selectedProject?.business_type || '',
            product_website: selectedProject?.product_website || '',
            companieshouse: selectedProject?.companieshouse ? String(selectedProject?.companieshouse) : '',
        };

        console.log('handleSetFormValues - newFormData:', newFormData);
        setFormData(newFormData);
        setQuillContent(selectedProject?.description || '');
        setUploadedFile();
    }, [selectedProject]);

    React.useEffect(() => {
        console.log('AddOrganisationDetail useEffect - selectedProject:', selectedProject);
        console.log('AddOrganisationDetail useEffect - selectedProject exists:', !!selectedProject);
        console.log('AddOrganisationDetail useEffect - component mounted:', isComponentMounted);
        if (selectedProject && isComponentMounted) {
            console.log('useEffect triggered with selectedProject:', selectedProject);
            handleSetFormValues();
        }
    }, [selectedProject, handleSetFormValues, isComponentMounted]);

    // Add a separate effect to monitor formData changes
    React.useEffect(() => {
        console.log('formData changed:', formData);
    }, [formData]);

    // Add a separate effect to monitor quillContent changes
    React.useEffect(() => {
        console.log('quillContent changed:', quillContent);
    }, [quillContent]);

    console.log('AddOrganisationDetail render - formData:', formData);
    console.log('AddOrganisationDetail render - quillContent:', quillContent);
    console.log('AddOrganisationDetail render - selectedProject:', selectedProject);
    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle}>Organisation details</h5>
            <form onSubmit={handleSubmit}>
                <div>
                    <div className="mb-4">
                        <p className={styles.fieldLabel}>
                            Organisation Name <span className={styles.required}>*</span>
                        </p>
                        <Input className={styles.inputField} required value={formData?.company_name} onChange={handleChange} name="company_name" />
                    </div>
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between">
                            <p className={styles.fieldLabel}>
                                Website <span className={styles.required}>*</span>
                            </p>
                            <Tooltip withArrow content="Please enter a valid company website address in the correct format, as it is used to gather additional data.">
                                <Info12Filled role="button" color="var(--Neutral-Gray-6)" />
                            </Tooltip>
                        </div>
                        <Input
                            className={styles.inputField}
                            required
                            type="url"
                            value={formData?.website}
                            onChange={handleChange}
                            name="website"
                            placeholder="https://www.example.com"
                            pattern="https?://.+"
                        />
                    </div>
                    <div className="row m-0 p-0 mb-4">
                        <div className="col-sm-12 col-md-6 ms-0 ps-0">
                            <p className={styles.fieldLabel}>Sector</p>
                            <Input placeholder="Software Development" className={styles.inputField} value={formData?.sector} onChange={handleChange} name="sector" />
                        </div>
                        <div className="col-sm-12 col-md-6 me-0 pe-0">
                            <p className={styles.fieldLabel}>Business Type</p>
                            <Select className={styles.selectField} value={formData?.business_type} onChange={handleChange} name="business_type" placeholder="">
                                <option>Select</option>
                                <option value="B2B">B2B</option>
                                <option value="B2C">B2C</option>
                            </Select>
                        </div>
                    </div>
                    <div className="row m-0 p-0 mb-4">
                        <div className="col-sm-12 col-md-6 ms-0 ps-0">
                            <p className={styles.fieldLabel}>Company size</p>
                            <Input className={styles.inputField} placeholder="180" value={formData?.company_size} onChange={handleChange} name="company_size" />
                        </div>
                        <div className="col-sm-12 col-md-6 me-0 pe-0">
                            <p className={styles.fieldLabel}>Number of employees</p>
                            <Input className={styles.inputField} placeholder="180" value={formData?.employees} onChange={handleChange} name="employees" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <p className={styles.fieldLabel} style={{ marginBottom: '-27px', marginTop: '7px' }}>
                            Business Description
                        </p>
                        <div className={styles.customReactQuill}>
                            <ReactQuill
                                modules={{
                                    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                }}
                                placeholder=""
                                theme="snow"
                                value={quillContent}
                                onChange={value => {
                                    console.log('ReactQuill onChange called with value:', value);
                                    setQuillContent(value);
                                }}
                            />
                        </div>
                    </div>
                    <div className="row m-0 p-0 mb-4">
                        <div className="col-sm-12 col-md-6 ms-0 ps-0">
                            <p className={styles.fieldLabel}>Year of Establishment</p>
                            <Input className={styles.inputField} value={formData?.established_year} onChange={handleChange} name="established_year" />
                        </div>
                        <div className="col-sm-12 col-md-6 me-0 pe-0">
                            <div className="d-flex align-items-center justify-content-between">
                                <p className={styles.fieldLabel}>Companies House ID</p>
                                <Search20Regular onClick={() => handleSearchOnWeb('Companies House ID')} role="button" color="var(--Brand-Secondary)" />
                            </div>
                            <Input className={styles.inputField} value={formData?.companieshouse} onChange={handleChange} name="companieshouse" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <p className={styles.fieldLabel}>Product Website</p>
                        <Input className={styles.inputField} value={formData?.product_website} onChange={handleChange} name="product_website" placeholder="" />
                    </div>
                    <div className="mb-4">
                        <p className={styles.fieldLabel}>Revenue</p>
                        <div className="d-flex">
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <div className={styles.currencyDiv}>
                                        {['£ GBP', '$ USD', '€ EUR'].find(item => item?.includes(formData?.currency))?.split(' ')[1]} <ChevronDown16Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={() => setFormData({ ...formData, currency: '£' })}>£ GBP</MenuItem>
                                        <MenuItem onClick={() => setFormData({ ...formData, currency: '$' })}>$ USD</MenuItem>
                                        <MenuItem onClick={() => setFormData({ ...formData, currency: '€' })}>€ EUR</MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Input className={styles.inputField} value={formData?.revenue} onChange={handleChange} name="revenue" type="number" placeholder="" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between">
                            <p className={styles.fieldLabel}>Logo</p>
                            <span className={styles.linkBtn} onClick={handleDivClick}>
                                Change
                            </span>
                        </div>
                        <div
                            onClick={handleDivClick}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{ background: formData?.base_logo && 'white' }}
                            className={`${styles.logoDiv} ${styles.uploadBox} ${isDragging ? styles.dragging : ''}`}>
                            {formData?.base_logo ? (
                                <img className={styles.logoImage} alt={'logo'} src={formData?.base_logo} />
                            ) : (
                                <span className={styles.uploadTextDragDrop}>
                                    Drag & drop file or
                                    <br />
                                    click to upload
                                    <br />
                                    <br />
                                    <small>(JPEG, PNG, JPG)</small>
                                </span>
                            )}

                            <input
                                multiple={false}
                                type="file"
                                accept="image/jpeg,image/png,image/jpg"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <p className={styles.fieldLabel}>LinkedIn Company Page</p>
                        <Input className={styles.inputField} value={formData?.linkedin} onChange={handleChange} name="linkedin" placeholder="www.linkedin.com/en-in/company-name" />
                    </div>

                    <div className={styles.buttonDiv}>
                        {(!compareObjectsValues(selectedProject, { ...formData, description: quillContent }) || uploadedFile) && (
                            <div className="d-flex align-items-center justify-content-between">
                                <button type="submit" disabled={!formData?.company_name || !formData?.website} className={styles.submitButton}>
                                    Save {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                                </button>
                                <span onClick={handleSetFormValues} className={styles.linkBtn}>
                                    Revert
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddOrganisationDetail;
